import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Flex, Heading, Icon, Popover, Text, Tooltip } from 'core/components';
import { TbChevronsRight } from 'react-icons/tb';
import { Field, SegmentedControl, Select, Switch } from 'core/form';
import $dataviews, { dataviewCategoryTitles, DataViewConfig, DataViewType } from 'app/stores/$dataviews';
import $metrics from 'app/stores/metrics/$metrics';
import { UdeFormProps } from 'app/views/boards/panels/ude/form.types';
import { usePanelEditContext } from 'app/views/boards/PanelEditContext';
import { useFormContext } from 'core/form/FormContext';
import Section from 'app/views/boards/SidebarSection';
import BoardPanelModel, { BoardPanelJson } from 'app/stores/boards/BoardPanelModel';
import { getEnumHealthyValue, isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import { CHART_VIZ_LIMIT_OPTIONS, TABLE_VIZ_LIMIT_OPTIONS } from 'app/stores/boards/panels/udeFields';
import { useDictionaryMeasurements } from 'app/stores/dictionary/useDictionaryMeasurements';
import UdeThresholdEditor from './UdeThresholdEditor';
import YAxisMetricSection from './YAxisMetricSection';
import { UdeFilterFormDisplay } from './filters/UdeFilterDisplay';
import UdeFilterModal from './filters/UdeFilterModal';

/**
 * Returns true when the metric details contain a non-empty Values map.
 * Used as an extra guard so the fallback path in isTimelineEligibleMetric
 * also requires real schema values before enabling the timeline view.
 */
function hasNonEmptyValuesMap(metricDetails: unknown): boolean {
  if (!metricDetails || typeof metricDetails !== 'object') {
    return false;
  }
  const values = (metricDetails as { Values?: unknown }).Values;
  return !!values && typeof values === 'object' && Object.keys(values as Record<string, unknown>).length > 0;
}

/**
 * Returns true if the metric should be eligible for the timeline view.
 * Requires a non-empty Values map from the measurement schema in all paths:
 * - either the metric is detected as an enum metric (has Values map), or
 * - the metric name matches a known status-like fallback AND the schema has
 *   returned Values (i.e. the backend has caught up).
 * The HealthyValue fallback map is used only for coloring in the renderer,
 * not for eligibility.
 */
function isTimelineEligibleMetric(metricName: string, metricDetails: unknown): boolean {
  return (
    isEnumItem(metricDetails as { Values?: Record<string | number, string> } | null) ||
    (getEnumHealthyValue(undefined, metricName) !== undefined && hasNonEmptyValuesMap(metricDetails))
  );
}

const ChartTypeKey = 'config.chart.type';

/** Display-only label: drop a trailing " Chart" suffix so the grid reads more compactly. */
function toDisplayLabel(label: string): string {
  return label.replace(/\s*Chart$/i, '').trim();
}

interface UdeChartSettingsFormProps {
  panel: BoardPanelModel;
  onToggleCollapse?: () => void;
}

type ChartTypeView = Omit<DataViewConfig<unknown>, 'category'> & {
  value: DataViewType;
  category?: (typeof dataviewCategoryTitles)[number];
};

/**
 * Returns true if the currently-selected metrics make Measurement-Specific
 * (timeline) views eligible. Mirrors the per-view disabled logic.
 */
function useMeasurementSpecificEligible(form: ReturnType<typeof useFormContext>): boolean {
  return useMemo(() => {
    const metrics = form?.getValue('config.data.metrics') as { name: string }[] | undefined;
    const measurement = form?.getValue('config.data.measurement') as string | undefined;
    if (!metrics?.length || !measurement) {
      return false;
    }
    const measurementModel = $metrics?.measurementModel?.(measurement);
    return metrics.some((m) => isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`)));
  }, [form]);
}

function FormChartTypeTileImpl({
  view,
  panel,
  onSelected
}: {
  view: ChartTypeView;
  panel: BoardPanelModel;
  onSelected?: () => void;
}) {
  const form = useFormContext();

  const onClick = useCallback(() => {
    if (!form) {
      return;
    }

    form.setValue(ChartTypeKey, view.value);

    // Timeline view requires "last" aggregation to preserve discrete state
    // transitions; force the metric's window.fn and rollup accordingly.
    // Use the first enum metric found (there may be non-enum metrics co-selected).
    if (view.value === 'timelineView') {
      const metrics = (form?.getValue('config.data.metrics') as { name: string }[]) || [];
      const measurement = form?.getValue('config.data.measurement') as string | undefined;
      const measurementModel = measurement ? $metrics.measurementModel?.(measurement) : undefined;
      const enumMetric = metrics.find((m) =>
        isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`))
      );
      const metricName = enumMetric?.name;
      if (metricName) {
        const currentWindowFn = (form?.getValue('config.data.window.fn') as Record<string, string>) || {};
        form?.setValue('config.data.window.fn', { ...currentWindowFn, [metricName]: 'last' });
        const currentRollups = (form?.getValue('config.data.rollups') as { metric: string; fn: string }[]) || [];
        const hasMetricRollup = currentRollups.some((r) => r.metric === metricName);
        const updatedRollups = hasMetricRollup
          ? currentRollups.map((r) => (r.metric === metricName ? { ...r, fn: 'last' } : r))
          : [...currentRollups, { metric: metricName, fn: 'last' }];
        form?.setValue('config.data.rollups', updatedRollups);
      }
    }

    /** Sync the form's config to the panel model (triggering history tracking) and kick off autoSave */
    const formValues = form.getValues() as BoardPanelJson;
    panel.applyQueryConfig({ ...panel.get('config'), ...formValues.config });
    if (form.onChange) {
      form.onChange({ form, formValues });
    }

    onSelected?.();
  }, [form, panel, view.value, onSelected]);

  const active = form?.getValue(ChartTypeKey) === view.value;

  let disabled = false;
  if (view.category === 'Measurement-Specific') {
    disabled = true;
    const metrics = form?.getValue('config.data.metrics') as { name: string }[];
    const measurement = form?.getValue('config.data.measurement') as string | undefined;
    if (metrics?.length >= 1 && measurement) {
      const measurementModel = $metrics?.measurementModel?.(measurement);
      const hasEnumMetric = metrics.some((m) =>
        isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`))
      );
      if (hasEnumMetric) {
        disabled = false;
      }
    }
    if (!active && disabled) {
      return null;
    }
  }

  const tile = (
    <Button
      minimal
      active={active}
      disabled={disabled && !active}
      onClick={onClick}
      width="100%"
      style={{ whiteSpace: 'normal' }}
    >
      <Flex flexDirection="row" alignItems="center" justifyContent="center" gap="6px" width="100%">
        <Icon icon={view.icon} size={16} />
        <Text textAlign="left" fontSize={12} width="100%">
          {toDisplayLabel(view.label)}
        </Text>
      </Flex>
    </Button>
  );

  if (view.category === 'Measurement-Specific' && disabled) {
    return <Tooltip content="Timeline view is only available for status-style metrics.">{tile}</Tooltip>;
  }
  return tile;
}

const FormChartTypeTile = observer(FormChartTypeTileImpl);

const HIDDEN_VIZ_LIMIT_TYPES: DataViewType[] = ['matrix', 'gauge', 'geoHeatMap', 'timelineView', 'horizon'];

const VizLimitSelect = observer(({ panel }: { panel: BoardPanelModel }) => {
  const form = useFormContext();
  const chartType = form?.getValue(ChartTypeKey) as DataViewType;
  const panelEditContext = usePanelEditContext();

  const isTable = chartType === 'table';
  const options = isTable ? TABLE_VIZ_LIMIT_OPTIONS : CHART_VIZ_LIMIT_OPTIONS;

  // Normalize viz.limit when chart type changes so the Select always shows a valid option
  useEffect(() => {
    if (!form) {
      return;
    }

    const current = form.getValue('config.data.viz.limit') as number;
    if (!options.some((o) => o.value === current)) {
      form.setValue('config.data.viz.limit', options[0].value);
    }
  }, [form, options]);

  const handleVizLimitChange = useCallback(() => {
    if (!form || !panel.queryModel) {
      return;
    }

    const vizLimit = form.getValue('config.data.viz.limit') as number;
    panel.queryModel.vizLimit = !isTable ? vizLimit : null;

    form.setValue('config.data.limit', Math.max(vizLimit, form.getValue('config.data.limit') as number));

    panelEditContext?.onRunQuery();
  }, [form, panel, panelEditContext, isTable]);

  if (HIDDEN_VIZ_LIMIT_TYPES.includes(chartType)) {
    return null;
  }

  return (
    <Flex alignItems="center" gap="4px" px="12px" pt={2}>
      <Text as="span" fontSize="12px">
        Show
      </Text>
      <Field name="config.data.viz.limit" showLabel={false} m={0} onChange={handleVizLimitChange} options={options}>
        <Select menuWidth={80} />
      </Field>
      <Text as="span" fontSize="12px">
        Series
      </Text>
    </Flex>
  );
});

const UdeChartTypeSelectorImpl = ({ panel }: { panel: BoardPanelModel }) => {
  const form = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const measurementSpecificEligible = useMeasurementSpecificEligible(form);

  const currentType = form?.getValue(ChartTypeKey) as DataViewType;
  const selectedView = $dataviews.viewTypeOptions.find((view) => view.value === currentType) as
    | ChartTypeView
    | undefined;

  const content = (
    <Box p={1} width={325}>
      <Flex flexDirection="column" gap="4px">
        {dataviewCategoryTitles.flatMap((category) => {
          const views = $dataviews.viewTypeOptions.filter(
            (view) =>
              view.category === category &&
              !view.hideFromViewMenu &&
              // Hide Measurement-Specific views unless eligible (or currently selected)
              (category !== 'Measurement-Specific' || measurementSpecificEligible || view.value === currentType)
          ) as ChartTypeView[];

          return views.map((view) => (
            <FormChartTypeTile key={view.value} view={view} panel={panel} onSelected={() => setIsOpen(false)} />
          ));
        })}
      </Flex>
    </Box>
  );

  return (
    <Section title="Type" mb={0} isCollapsible={false} defaultIsOpen>
      <Box px="12px">
        <Popover
          isOpen={isOpen}
          onInteraction={(nextOpen: boolean) => setIsOpen(nextOpen)}
          content={content}
          placement="bottom-start"
          fill
        >
          <Button
            small
            fill
            textAlign="left"
            onClick={() => setIsOpen((open) => !open)}
            rightIcon="caret-down"
            icon={selectedView?.icon && <Icon icon={selectedView.icon} size={14} />}
          >
            {selectedView ? toDisplayLabel(selectedView.label) : 'Select chart type'}
          </Button>
        </Popover>
      </Box>
      <VizLimitSelect panel={panel} />
    </Section>
  );
};

const UdeChartTypeSelector = observer(UdeChartTypeSelectorImpl);

function UdeChartSettingsForm(props: UdeChartSettingsFormProps) {
  const { panel, onToggleCollapse } = props;
  const form = useFormContext() as UdeFormProps['form'] | null;
  const [isEditingUnitFilters, setIsEditingUnitFilters] = useState(false);
  const chartType = form?.getValue(ChartTypeKey);

  const measurementName = form?.getValue('config.data.measurement');
  const hasUnitFilters = !!form?.getValue('config.data.filters.metrics.filterGroups')?.length;

  const { measurements, measurementHash } = useDictionaryMeasurements();

  const handleToggleUnitFilterModal = useCallback((e?: React.MouseEvent<HTMLElement>) => {
    e?.stopPropagation();
    setIsEditingUnitFilters((prev) => !prev);
  }, []);

  return (
    <Flex
      flexDirection="column"
      width="100%"
      height="100%"
      bg="cardBackground"
      borderRadius="6px 0 6px 6px"
      border="thin"
      style={{ scrollbarWidth: 'thin' }}
      overflowY="auto"
    >
      <Flex
        alignItems="center"
        pl={2}
        pr={0}
        py={1}
        flexShrink={0}
        borderBottom="thin"
        bg="cardBackground"
        style={{ position: 'sticky', top: 0, zIndex: 1 }}
      >
        <Heading level={6} m={0} fontSize={14} fontWeight="bold" flex={1}>
          Chart
        </Heading>
        {onToggleCollapse && (
          <Tooltip content="Close Chart options">
            <Button minimal icon={TbChevronsRight} onClick={onToggleCollapse} aria-label="Close Chart options" />
          </Tooltip>
        )}
      </Flex>

      <UdeChartTypeSelector panel={panel} />

      {measurementName && (
        <>
          <Section
            title="Unit Filters"
            actions={
              <Button
                small
                minimal
                intent="primary"
                text={hasUnitFilters ? 'Edit' : 'Add'}
                onClick={handleToggleUnitFilterModal}
                ml="auto"
              />
            }
          >
            <Box mx={2}>
              {!hasUnitFilters && (
                <Text muted small>
                  No filters applied
                </Text>
              )}
              <UdeFilterFormDisplay type="metrics" measurementName={measurementName} />
            </Box>
          </Section>
          <UdeFilterModal
            title="Unit Filters"
            fieldName="config.data.filters.metrics"
            isOpen={isEditingUnitFilters}
            onClose={handleToggleUnitFilterModal}
            measurement={measurementName}
            measurements={measurements}
            measurementHash={measurementHash}
          />
        </>
      )}

      {chartType !== 'table' && (
        <>
          <YAxisMetricSection panel={panel} />
          <Section
            title="Table"
            actions={
              <Flex alignItems="center" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <Field name="config.chart.legend.enable" showLabel={false} m={0}>
                  <Switch showSwitchLabel={false} aria-label="Enable table" />
                </Field>
              </Flex>
            }
          >
            <Flex flexDirection="column" px={2} gap={1} pt={1}>
              <Field name="config.chart.legend.position" m={0}>
                <SegmentedControl small fill />
              </Field>
              <Field name="config.chart.legend.mode" m={0}>
                <SegmentedControl small fill />
              </Field>
            </Flex>
          </Section>
        </>
      )}

      <Section
        title="Thresholds"
        actions={
          <Flex alignItems="center" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Field name="config.chart.thresholds.enabled" showLabel={false} m={0}>
              <Switch showSwitchLabel={false} aria-label="Enable thresholds" />
            </Field>
          </Flex>
        }
      >
        <Flex flexDirection="column" px={2} gap={1} pt={1}>
          <UdeThresholdEditor />
        </Flex>
      </Section>
    </Flex>
  );
}

export default observer(UdeChartSettingsForm);
